import { useTranslation } from 'react-i18next'
import { ChevronsUpDown, Building2, Check } from 'lucide-react'

import { useEntity } from '@/contexts/EntityContext'
import { useSidebar } from '@/components/ui/sidebar'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export function EntitySwitcher() {
    const { t } = useTranslation()
    const { currentEntity, entities, setCurrentEntity } = useEntity()
    const { isMobile, state } = useSidebar()

    const isCollapsed = state === 'collapsed'

    if (!currentEntity) return null

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className="w-full rounded-xl ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 group/switcher"
            >
                <div
                    className={`flex items-center gap-2 rounded-xl text-left text-sm transition-colors duration-200 ${isCollapsed
                        ? 'justify-center p-1'
                        : 'bg-sidebar px-3 py-2 hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent'
                        }`}
                >
                    <div className={`flex shrink-0 items-center justify-center rounded-lg text-sidebar-primary transition-colors duration-200 ${isCollapsed
                        ? 'h-10 w-10 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                        : 'h-6 w-6 group-hover/switcher:text-sidebar-accent-foreground group-data-[state=open]/switcher:text-sidebar-accent-foreground'
                        }`}>
                        <Building2 className={isCollapsed ? 'h-5 w-5' : 'h-4 w-4'} />
                    </div>
                    {!isCollapsed && (
                        <>
                            <div className="flex-1 min-w-0">
                                <p className="truncate font-medium text-sidebar-foreground">
                                    {currentEntity.internalName || currentEntity.entityName}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                    {currentEntity.roleName}
                                </p>
                            </div>
                            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                        </>
                    )}
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-xl"
                align="start"
                side={isMobile ? 'bottom' : 'right'}
                sideOffset={4}
            >
                <DropdownMenuLabel className="text-xs font-semibold text-foreground bg-muted/40 -mx-1 -mt-1 px-3 py-2.5 rounded-t-xl mb-1 border-b">
                    {t('entity.selectEntity', 'Sélectionnez une entité')}
                </DropdownMenuLabel>

                {entities.map((entity) => (
                    <DropdownMenuItem
                        key={entity.entityId}
                        onClick={() => setCurrentEntity(entity)}
                        className="gap-2 p-2 rounded-lg focus:bg-sidebar-accent focus:text-sidebar-accent-foreground"
                    >
                        <div className="flex h-6 w-6 items-center justify-center rounded-md">
                            <Building2 className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">{entity.internalName || entity.entityName}</p>
                            <p className="truncate text-xs text-muted-foreground">
                                {entity.roleName}
                            </p>
                        </div>
                        {entity.entityId === currentEntity.entityId && (
                            <Check className="h-4 w-4" />
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
